import type { NavbarLogo } from '../schemas/validation';

export type NavbarLogoMode = 'text' | 'image';

const LEGACY_BOTH_NOTICE_KEY_PREFIX = 'competepages.navbar.legacyBothNotice.';

/** Legacy sites may still have mode "both" in stored JSON. */
export function isLegacyBothLogo(logo?: { mode?: string } | null): boolean {
  return logo?.mode === 'both';
}

/**
 * When opening the logo editor, map legacy "both" to a single tab (prefer image if present).
 */
export function prepareLogoForEditor(logo?: Partial<NavbarLogo>): {
  logoData: Partial<NavbarLogo>;
  activeTab: NavbarLogoMode;
} {
  if (!logo || Object.keys(logo).length === 0) {
    return { logoData: {}, activeTab: 'text' };
  }

  if (logo.mode !== 'both') {
    const tab: NavbarLogoMode = logo.mode === 'image' ? 'image' : 'text';
    return { logoData: { ...logo }, activeTab: tab };
  }

  const hasImage = !!(logo.imageUrl || logo.cloudfrontUrl || logo.mediaId);
  if (hasImage) {
    return { logoData: { ...logo, mode: 'image' }, activeTab: 'image' };
  }
  return { logoData: { ...logo, mode: 'text' }, activeTab: 'text' };
}

/** Persist only fields relevant to the selected mode. */
export function prepareLogoForSave(
  logoData: Partial<NavbarLogo>,
  mode: NavbarLogoMode
): NavbarLogo {
  const linkUrl = logoData.linkUrl;
  const linkTarget = logoData.linkTarget ?? '_self';

  if (mode === 'image') {
    return {
      mode: 'image',
      imageUrl: logoData.imageUrl,
      cloudfrontUrl: logoData.cloudfrontUrl,
      imageAlt: logoData.imageAlt,
      imageWidth: logoData.imageWidth,
      imageHeight: logoData.imageHeight,
      lockAspectRatio: logoData.lockAspectRatio,
      originalWidth: logoData.originalWidth,
      originalHeight: logoData.originalHeight,
      mediaId: logoData.mediaId,
      s3Key: logoData.s3Key,
      linkUrl,
      linkTarget,
    } as NavbarLogo;
  }

  return {
    mode: 'text',
    text: logoData.text,
    textSize: logoData.textSize,
    textWeight: logoData.textWeight,
    textColor: logoData.textColor,
    linkUrl,
    linkTarget,
  } as NavbarLogo;
}

export function getLegacyBothNoticeStorageKey(siteId: string): string {
  return `${LEGACY_BOTH_NOTICE_KEY_PREFIX}${siteId}`;
}

export function hasSeenLegacyBothNotice(siteId: string): boolean {
  if (typeof window === 'undefined' || !siteId) return true;
  try {
    return window.localStorage.getItem(getLegacyBothNoticeStorageKey(siteId)) === '1';
  } catch {
    return true;
  }
}

export function markLegacyBothNoticeSeen(siteId: string): void {
  if (typeof window === 'undefined' || !siteId) return;
  try {
    window.localStorage.setItem(getLegacyBothNoticeStorageKey(siteId), '1');
  } catch {
    // ignore quota / private mode
  }
}

/**
 * After converting a legacy combined logo to text or image, preserve brand text on the navbar
 * when the user chooses image-only and siteName is empty.
 */
export function applyLegacyBothNavbarMigration<T extends { siteName?: string; logo?: NavbarLogo }>(
  navbarData: T,
  savedLogo: NavbarLogo,
  legacyLogoText?: string
): T {
  const legacyText = legacyLogoText?.trim();
  if (!legacyText || savedLogo.mode !== 'image') {
    return { ...navbarData, logo: savedLogo };
  }

  const siteName = navbarData.siteName?.trim();
  return {
    ...navbarData,
    logo: savedLogo,
    siteName: siteName || legacyText,
  };
}
